import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, User, Crown, Shield } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getUserByIdAction } from "../../../store/slices/users/usersSliceActions";
import { colors } from "../../../constants/colors";
import UserProfileSection from "./UserProfileSection";
import DailyUsageSection from "./DailyUsageSection";
import MealsSection from "./MealsSection";
import SubscriptionsSection from "./SubscriptionsSection";
import SubscriptionDailyUsageSection from "./SubscriptionDailyUsageSection";
import UserPlanSection from "./UserPlanSection";
import DietPreferencesSection from "./DietPreferencesSection";
import WorkoutPlansSection from "./WorkoutPlansSection";
import PremiumAccessModal from "./PremiumAccessModal";

const UserDetail = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { selectedUser } = useAppSelector((state) => state.users);
  const { subscriptionsData } = useAppSelector((state) => state.subscriptions);

  // Check if user has admin-granted premium subscription
  const hasAdminGrantedPremium = () => {
    if (!subscriptionsData?.subscriptions) return false;

    return subscriptionsData.subscriptions.some(
      (sub) =>
        sub.isActive &&
        sub.currentStatus === "active" &&
        (sub.store === "ADMIN_GRANT" || sub.store === "admin_panel")
    );
  };

  const adminGrantedSubscription = subscriptionsData?.subscriptions?.find(
    (sub) =>
      sub.isActive &&
      sub.currentStatus === "active" &&
      (sub.store === "ADMIN_GRANT" || sub.store === "admin_panel")
  );

  // Check authentication and load user data
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/admin");
      return;
    }
  }, [isAuthenticated, navigate]);

  // Separate effect for loading user data
  useEffect(() => {
    const selectedUserUid = selectedUser?.uid;

    // Load user data if we have a uid and no selected user or different user
    if (uid && (!selectedUser || selectedUserUid !== uid)) {
      dispatch(getUserByIdAction(uid));
    }
  }, [uid, selectedUser, dispatch]);

  // Styles
  const containerStyle = {
    minHeight: "calc(100vh - 80px)",
    backgroundColor: colors.extraLightGray,
    padding: "2rem",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: colors.white,
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    flexWrap: "wrap",
  };

  const backButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    backgroundColor: colors.lightGray,
    color: colors.darkGray,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
  };

  const titleStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    color: colors.darkGreen,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const premiumButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    backgroundColor: hasAdminGrantedPremium()
      ? colors.lightGray
      : colors.darkGreen,
    color: hasAdminGrantedPremium() ? colors.darkGray : colors.white,
    border: "none",
    borderRadius: "8px",
    cursor: hasAdminGrantedPremium() ? "default" : "pointer",
    fontWeight: "600",
    transition: "all 0.3s ease",
    opacity: hasAdminGrantedPremium() ? 0.7 : 1,
  };

  const adminBadgeStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.3rem",
    padding: "0.4rem 0.8rem",
    backgroundColor: "#e7f3ff",
    color: "#0066cc",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: "600",
    border: "1px solid #b3d9ff",
  };

  if (!sessionStorage.getItem("adminAuthenticated")) {
    return null;
  }

  return (
    <div style={containerStyle} className="admin-responsive-container">
      <div style={headerStyle} className="admin-header-responsive">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <button
            onClick={() => navigate("/admin/overview")}
            style={backButtonStyle}
            className="admin-back-button"
          >
            <ArrowLeft size={18} />
            Back to Users
          </button>
          <h1 style={titleStyle} className="admin-title">
            <User size={32} />
            User Details
          </h1>
        </div>

        {uid && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
              width: "100%",
            }}
            className="admin-button-group"
          >
            {hasAdminGrantedPremium() && (
              <div
                style={adminBadgeStyle}
                title={`Admin granted premium expires: ${
                  adminGrantedSubscription?.expirationDate
                    ? new Date(
                        adminGrantedSubscription.expirationDate
                      ).toLocaleDateString()
                    : "N/A"
                }`}
              >
                <Shield size={14} />
                Admin Premium Active
              </div>
            )}
            <button
              onClick={() =>
                !hasAdminGrantedPremium() && setIsPremiumModalOpen(true)
              }
              style={premiumButtonStyle}
              title={
                hasAdminGrantedPremium()
                  ? "User already has admin-granted premium access"
                  : "Grant Premium Access"
              }
              disabled={hasAdminGrantedPremium()}
            >
              <Crown size={18} />
              {hasAdminGrantedPremium()
                ? "Premium Access Granted"
                : "Give Premium Access"}
            </button>
          </div>
        )}
      </div>

      {/* User Profile Section */}
      <UserProfileSection uid={uid} />

      {/* Daily Usage Section */}
      <DailyUsageSection uid={uid} />

      {/* Meals Section */}
      <MealsSection uid={uid} />

      {/* Subscriptions Section */}
      <SubscriptionsSection uid={uid} />

      {/* Subscription Daily Usage Section */}
      <SubscriptionDailyUsageSection uid={uid} />

      {/* User Plan Section */}
      <UserPlanSection uid={uid} />

      {/* Diet Preferences Section */}
      <DietPreferencesSection uid={uid} />

      {/* Workout Plans Section */}
      <WorkoutPlansSection uid={uid} />

      {/* Milestones Section */}
      {/* <MilestonesSection uid={uid} /> */}

      {/* Premium Access Modal */}
      <PremiumAccessModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
        userId={uid}
      />
    </div>
  );
};

export default UserDetail;
